import React, { useState, useEffect } from 'react';
import ChannelSelector from './ChannelSelector';
import AvatarModal from './AvatarModal';
import { uploadAvatar, deleteAvatar } from '../services/api';
import '../styles/Header.css';

function Header({ user, onLogout, channels, selectedChannel, onSelectChannel, unreadCounts = [], isConnected = true }) {
  const [deferredPrompt, setDeferredPrompt] = useState(null);
  const [isStandalone, setIsStandalone] = useState(false);
  const [showInstallModal, setShowInstallModal] = useState(false);
  const [isAvatarModalOpen, setIsAvatarModalOpen] = useState(false);
  const [avatarUrl, setAvatarUrl] = useState(user?.avatar || null);
  const [showLogoutConfirm, setShowLogoutConfirm] = useState(false);

  useEffect(() => {
    setAvatarUrl(user?.avatar || null);
  }, [user?.avatar]);

  useEffect(() => {
    // Check if app is already running in standalone mode
    const standalone = window.matchMedia('(display-mode: standalone)').matches ||
                       window.navigator.standalone === true;
    setIsStandalone(standalone);

    const handleBeforeInstallPrompt = (e) => {
      e.preventDefault();
      setDeferredPrompt(e);
    };

    window.addEventListener('beforeinstallprompt', handleBeforeInstallPrompt);
    return () => window.removeEventListener('beforeinstallprompt', handleBeforeInstallPrompt);
  }, []);

  const handleInstallClick = async () => {
    if (deferredPrompt) {
      deferredPrompt.prompt();
      const { outcome } = await deferredPrompt.userChoice;
      if (outcome === 'accepted') console.log('PWA installed');
      setDeferredPrompt(null);
    } else {
      setShowInstallModal(true);
    }
  };

  const handleAvatarUpload = async (file) => {
    const formData = new FormData();
    formData.append('avatar', file);
    const data = await uploadAvatar(formData);
    setAvatarUrl(data.avatarUrl + '?t=' + Date.now());
  };

  const handleAvatarDelete = async () => {
    await deleteAvatar();
    setAvatarUrl(null);
  };

  const getInitials = (name) => {
    if (!name) return '?';
    const parts = name.trim().split(' ');
    if (parts.length >= 2) return (parts[0][0] + parts[1][0]).toUpperCase();
    return name[0].toUpperCase();
  };

  const isIOS = () => /iPad|iPhone|iPod/.test(navigator.userAgent) && !window.MSStream;
  const isAndroid = () => /Android/.test(navigator.userAgent);

  const getInstallInstructions = () => {
    const hostname = window.location.hostname;
    const isLocal = hostname.startsWith('192.168.') || hostname === 'localhost';
    const versionText = isLocal ? 'локальную' : 'интернет';

    if (isIOS()) {
      return {
        title: `📲 Установка ${versionText} версии (iOS)`,
        steps: [
          'Нажмите кнопку "Поделиться" (⎙) внизу экрана',
          'Прокрутите вниз и выберите "На экран Домой"',
          'Нажмите "Добавить"',
          `Ярлык "Приют Чат" появится на главном экране`
        ]
      };
    } else if (isAndroid()) {
      return {
        title: `📲 Установка ${versionText} версии (Android)`,
        steps: [
          'Нажмите меню браузера (три точки ⋮) в правом верхнем углу',
          'Выберите "Установить приложение" или "Добавить на главный экран"',
          'В появившемся окне нажмите "Установить"',
          `Приложение появится на главном экране телефона`
        ]
      };
    } else {
      return {
        title: `📲 Установка ${versionText} версии`,
        steps: [
          'Нажмите меню браузера (⋮ или ⋯)',
          'Выберите "Установить приложение" или "Добавить на главный экран"',
          'Следуйте инструкциям на экране'
        ]
      };
    }
  };

  const shouldShowButton = !isStandalone;
  const instructions = getInstallInstructions();

  return (
    <>
      <header className="app-header">
        <div className="header-left">
          {channels && selectedChannel && (
            <ChannelSelector
              channels={channels}
              selectedChannel={selectedChannel}
              onSelectChannel={onSelectChannel}
              unreadCounts={unreadCounts}
            />
          )}
        </div>

        <div className="header-right">
          {shouldShowButton && (
            <button className="btn-install" onClick={handleInstallClick} title="Установить приложение">
              📲
            </button>
          )}
          <div className="user-info">
            <span className="user-name">{user.displayName}</span>
            <span className="user-role">{getRoleLabel(user.role)}</span>
          </div>

          <div style={{ position: 'relative', display: 'inline-flex' }}>
            <button
              className="btn-avatar"
              onClick={() => setIsAvatarModalOpen(true)}
              title="Фото профиля"
            >
              {avatarUrl ? (
                <img src={avatarUrl} alt="avatar" className="header-avatar-img" />
              ) : (
                <span className="header-avatar-initials">{getInitials(user.displayName)}</span>
              )}
            </button>
            <span style={{
              position: 'absolute',
              bottom: 0,
              right: 0,
              width: 9,
              height: 9,
              borderRadius: '50%',
              background: isConnected ? '#43a047' : '#e53935',
              border: '1.5px solid #fff',
              pointerEvents: 'none',
            }} />
          </div>

          <button className="btn-logout" onClick={() => setShowLogoutConfirm(true)}>
            Выйти
          </button>
        </div>
      </header>

      {isAvatarModalOpen && (
        <AvatarModal
          currentAvatar={avatarUrl}
          displayName={user.displayName}
          onClose={() => setIsAvatarModalOpen(false)}
          onUpload={handleAvatarUpload}
          onDelete={handleAvatarDelete}
        />
      )}

      {/* Logout Confirmation Modal */}
      {showLogoutConfirm && (
        <div className="install-modal-overlay" onClick={() => setShowLogoutConfirm(false)}>
          <div className="install-modal" onClick={(e) => e.stopPropagation()}>
            <div className="install-modal-header">
              <h2>Выход из системы</h2>
              <button className="install-modal-close" onClick={() => setShowLogoutConfirm(false)}>
                ✕
              </button>
            </div>
            <div className="install-modal-body">
              <p>Вы уверены, что хотите выйти?</p>
            </div>
            <div className="install-modal-footer" style={{ display: 'flex', gap: '10px' }}>
              <button className="install-modal-button" style={{ background: '#e53935' }} onClick={() => { setShowLogoutConfirm(false); onLogout(); }}>
                Да, выйти
              </button>
              <button className="install-modal-button" onClick={() => setShowLogoutConfirm(false)}>
                Отмена
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Install Instructions Modal */}
      {showInstallModal && (
        <div className="install-modal-overlay" onClick={() => setShowInstallModal(false)}>
          <div className="install-modal" onClick={(e) => e.stopPropagation()}>
            <div className="install-modal-header">
              <h2>{instructions.title}</h2>
              <button className="install-modal-close" onClick={() => setShowInstallModal(false)}>
                ✕
              </button>
            </div>
            <div className="install-modal-body">
              <ol className="install-steps">
                {instructions.steps.map((step, index) => (
                  <li key={index}>{step}</li>
                ))}
              </ol>
              <div className="install-modal-note">
                💡 После установки приложение будет работать как обычное приложение на вашем устройстве
              </div>
            </div>
            <div className="install-modal-footer">
              <button className="install-modal-button" onClick={() => setShowInstallModal(false)}>
                Понятно
              </button>
            </div>
          </div>
        </div>
      )}
    </>
  );
}

function getRoleLabel(role) {
  const labels = {
    admin: 'Администратор',
    moderator: 'Модератор',
    user: 'Сотрудник'
  };
  return labels[role] || role;
}

export default Header;
